import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SectionHeading } from "./SectionHeading";
import { setJsonLd } from "@/hooks/useCanonical";
import { fadeUp } from "@/lib/animations";

const faqs = [
  {
    q: "O que é a entrevista médica qualificada?",
    a: (
      <>
        É uma entrevista conduzida por enfermeiros avaliadores no momento da
        adesão ao plano de saúde, com análise da Declaração de Saúde, apuração
        clínica e identificação de morbidades e CID. O resultado é um parecer
        técnico que apoia a operadora na qualificação do risco desde a
        contratação.{" "}
        <a
          href="/solucoes/entrevista-medica-qualificada"
          className="text-[#3532B3] font-semibold hover:underline"
        >
          Conheça o serviço em detalhes
        </a>
        .
      </>
    ),
    plain:
      "É uma entrevista conduzida por enfermeiros avaliadores no momento da adesão ao plano de saúde, com análise da Declaração de Saúde, apuração clínica e identificação de morbidades e CID. O resultado é um parecer técnico que apoia a operadora na qualificação do risco desde a contratação.",
  },
  {
    q: "Quais serviços a Previsa Saúde oferece?",
    a: (
      <>
        Entrevista médica qualificada,{" "}
        <a
          href="/solucoes/analise-declaracao-saude"
          className="text-[#3532B3] font-semibold hover:underline"
        >
          apoio ao preenchimento e análise da Declaração de Saúde (DS)
        </a>
        , parecer técnico,{" "}
        <a
          href="/solucoes/gestao-cpt"
          className="text-[#3532B3] font-semibold hover:underline"
        >
          gestão de CPT
        </a>{" "}
        (cobertura parcial temporária) e inteligência de carteira, com
        rastreabilidade e segurança jurídica em todas as etapas.
      </>
    ),
    plain:
      "Entrevista médica qualificada, apoio ao preenchimento e análise da Declaração de Saúde (DS), parecer técnico, gestão de CPT (cobertura parcial temporária) e inteligência de carteira, com rastreabilidade e segurança jurídica em todas as etapas.",
  },
  {
    q: "Quem pode contratar a Previsa Saúde?",
    a: "Operadoras de planos de saúde, administradoras de benefícios e demais organizações da saúde suplementar que buscam qualificar o risco assistencial desde a adesão.",
    plain:
      "Operadoras de planos de saúde, administradoras de benefícios e demais organizações da saúde suplementar que buscam qualificar o risco assistencial desde a adesão.",
  },
  {
    q: "A Previsa atende em todo o Brasil?",
    a: "Sim. A operação é remota, por videoatendimento, com capacidade para 6.000 entrevistas por mês. A sede fica em Vila Velha/ES.",
    plain:
      "Sim. A operação é remota, por videoatendimento, com capacidade para 6.000 entrevistas por mês. A sede fica em Vila Velha/ES.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.plain },
  })),
};

export const FAQ = () => {
  useEffect(() => setJsonLd("faq-schema", faqSchema), []);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-16 md:py-24 bg-[#F6F8FB]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <SectionHeading
          overline="Perguntas frequentes"
          title="Dúvidas comuns sobre a"
          highlight="Previsa Saúde"
          testid="faq-heading"
        />

        <motion.div {...fadeUp} className="mt-10 max-w-3xl">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((f, idx) => (
              <AccordionItem
                key={f.q}
                value={`faq-${idx}`}
                data-testid={`faq-item-${idx}`}
                className="bg-white rounded-2xl border border-[#E5EAF2] px-6 data-[state=open]:border-[#3787F5]/40 data-[state=open]:shadow-[0_12px_32px_rgba(53,50,179,0.08)] transition-all"
              >
                <AccordionTrigger className="text-left text-base font-bold text-[#1F2937] hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[15px] text-[#64748B] leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};
